import { State } from 'src/redux/reducers/RootReducer';
import makeActionCreator from 'src/lib/makeActionCreator';
import {
  cancelPaymentData,
  cancelTaxReceiptData,
  getPaymentData,
  postPaymentData,
  postPurchaseData,
  putPaymentData,
  refundPaymentData,
} from 'src/apis/PaymentAPI';
import { setFormStatus } from 'src/redux/actions/formActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { setError } from 'src/redux/actions/errorsActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { resetSelections } from 'src/redux/actions/selectionActions';
import { closeModal } from 'src/redux/actions/modalActions';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  CancelPaymentItem,
  PAYMENT_TYPE,
  PaymentModel,
  PurchasePayload,
} from 'src/models/PaymentModel';
import { MODALS } from 'src/models/ModalModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { DIALOGS } from 'src/models/DialogModel';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { FORM, FORM_STATUS } from 'src/constants/Form';
// eslint-disable-next-line import/no-extraneous-dependencies
import { toOrgTimezone } from 'src/lib/DateHelper';
import Debug from 'src/lib/Debug';
import { getContactAction } from 'src/redux/actions/contactActions';
import {
  getContactBankAccountAction,
  getContactCreditCardAction,
} from 'src/redux/actions/paymentInfoAction';

export const UPDATE_PAYMENT = 'UPDATE_PAYMENT';
export const DELETE_PAYMENTS = 'DELETE_PAYMENTS';

export const updatePaymentAction = makeActionCreator(UPDATE_PAYMENT);
export const deletePaymentsAction = makeActionCreator(DELETE_PAYMENTS);

export const normalizePayment = (data: any, timezone: string) => {
  const payment = { ...data } as PaymentModel;
  payment.paid_date = toOrgTimezone(data.paid_date, timezone) || null;
  payment.processed_date = toOrgTimezone(data.processed_date, timezone) || null;
  payment.scheduled_date = toOrgTimezone(data.scheduled_date, timezone) || null;
  return payment;
};

export const cancelPaymentAction: (paiements:CancelPaymentItem[]) => ThunkedAction<State> =
  (payments:CancelPaymentItem[]) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.cancel_payment]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      dispatch(setAlert({
        type: ALERT_TYPE.download,
        code: 'messages.processing',
      }));
      const response = await cancelPaymentData(payments, organisation);
      if (response.success) {
        dispatch(setFormStatus({ [FORM.cancel_payment]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.cancel_payment',
        }));
        dispatch(deletePaymentsAction(payments));
        dispatch(closeModal({ modal: MODALS.cancelPayments }));
        dispatch(getEntitiesAction(ENTITIES.payments));
        dispatch(resetSelections({ entity: ENTITIES.payments }));
        dispatch(closeDialog({ dialog: DIALOGS.cancelPayments }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.cancel_payment',
        }));
        dispatch(closeModal({ modal: MODALS.cancelPayments }));
        dispatch(setFormStatus({ [FORM.cancel_payment]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
    }
  };

export const refundPaymentAction: (id: number) => ThunkedAction<State> =
(id: number) => async (dispatch: any, getState: any) => {
  if (!id) {
    return;
  }
  try {
    dispatch(setFormStatus({ [FORM.refund_payment]: FORM_STATUS.processing }));
    const organisation = getState().currentOrganisation.id;
    const timezone = getState().data.timezones[getState().organisation.timezoneId];
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.processing',
    }));
    const response = await refundPaymentData(id, organisation);
    if (response.success) {
      const { data } = response;
      dispatch(updatePaymentAction({ id, payment: normalizePayment(data, timezone) }));
      dispatch(getEntitiesAction(ENTITIES.payments));
      dispatch(setFormStatus({ [FORM.refund_payment]: FORM_STATUS.success }));
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.payment_refund',
      }));
      dispatch(closeDialog({ dialog: DIALOGS.refundPayments }));
    } else if (response.status === 400) {
      dispatch(setError({ [FORM.refund_payment]: response.errors.fields }));
      dispatch(setFormStatus({ [FORM.refund_payment]: FORM_STATUS.error }));
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: Object.values(response.errors)[0],
      }));
      dispatch(closeDialog({ dialog: DIALOGS.refundPayments }));
    } else {
      dispatch(setFormStatus({ [FORM.refund_payment]: FORM_STATUS.error }));
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.payment_refund_error',
      }));
      dispatch(closeDialog({ dialog: DIALOGS.refundPayments }));
    }
  } catch (e) { /* Log the error here */
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.payment_refund_error',
    }));
    dispatch(setFormStatus({ [FORM.refund_payment]: FORM_STATUS.error }));
  }
};

export const cancelTaxReceiptAction: (paymentIds: number[]) => ThunkedAction<State> =
  (paymentIds: number[]) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.cancel_tax_receipt]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      dispatch(setAlert({
        type: ALERT_TYPE.download,
        code: 'messages.processing',
      }));
      const response = await cancelTaxReceiptData(paymentIds, organisation);
      if (response.success) {
        dispatch(setFormStatus({ [FORM.cancel_tax_receipt]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.cancel_tax_receipt_success',
        }));
        dispatch(getEntitiesAction(ENTITIES.payments));
        dispatch(resetSelections({ entity: ENTITIES.payments }));
        dispatch(closeDialog({ dialog: DIALOGS.cancelTaxReceipt }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.cancel_tax_receipt_error',
        }));
        dispatch(setFormStatus({ [FORM.cancel_tax_receipt]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setFormStatus({ [FORM.cancel_tax_receipt]: FORM_STATUS.error }));
    }
  };

export const postPaymentAction:
(payload: PaymentModel, close: boolean) => ThunkedAction<State> =
  (payload: PaymentModel, close) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.make_payment]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await postPaymentData(organisation, payload);
      if (response.success) {
        dispatch(getEntitiesAction(ENTITIES.invoices));
        dispatch(setFormStatus({ [FORM.make_payment]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.payment_saved',
        }));
        if (close) {
          dispatch(closeModal({ modal: MODALS.makePayment }));
        }
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.make_payment]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.make_payment]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: Object.values(response.errors)[0],
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.invoice_saved_error',
        }));
        dispatch(setFormStatus({ [FORM.reset_password]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
    }
  };

export const getPaymentAction: (id: number) => ThunkedAction<State> =
(id: number) => async (dispatch: any, getState: any) => {
  if (!id) {
    return;
  }
  try {
    dispatch(setFormStatus({ [FORM.get_payment]: FORM_STATUS.loading }));
    const organisation = getState().currentOrganisation.id;
    const timezone = getState().data.timezones[getState().organisation.timezoneId];
    const response = await getPaymentData(id, organisation);
    if (!response.success) {
      dispatch(setFormStatus({ [FORM.get_payment]: FORM_STATUS.error }));
      return;
    }
    const { data } = response;
    const payment = normalizePayment(data, timezone);
    dispatch(updatePaymentAction({ id, payment }));
    if (payment.contact_id) {
      await dispatch(getContactAction(payment.contact_id, FORM.get_payment));
    }
    if (payment.seller_id) {
      await dispatch(getContactAction(payment.seller_id, FORM.get_payment));
    }
    if (!payment.contact_id) {
      dispatch(setFormStatus({ [FORM.get_payment]: FORM_STATUS.success }));
      return;
    }
    if (payment.payment_type === PAYMENT_TYPE.cc) {
      await dispatch(getContactCreditCardAction(payment.contact_id));
    }
    if (payment.payment_type === PAYMENT_TYPE.eft) {
      await dispatch(getContactBankAccountAction(payment.contact_id));
    }
    dispatch(setFormStatus({ [FORM.get_payment]: FORM_STATUS.success }));
  } catch (e) { /* Log the error here */
    dispatch(setFormStatus({ [FORM.get_payment]: FORM_STATUS.error }));
  }
};

export const putPaymentAction: (id: number, payment: PaymentModel) => ThunkedAction<State> =
(id: number, payment: PaymentModel) => async (dispatch: any, getState: any) => {
  if (!id) {
    return;
  }
  try {
    dispatch(setFormStatus({ [FORM.put_payment]: FORM_STATUS.processing }));
    const organisation = getState().currentOrganisation.id;
    const timezone = getState().data.timezones[getState().organisation.timezoneId];
    const response = await putPaymentData(id, payment, organisation);
    if (response.success) {
      const { data } = response;
      dispatch(updatePaymentAction({ id, payment: normalizePayment(data, timezone) }));
      dispatch(getEntitiesAction(ENTITIES.payments));
      dispatch(setFormStatus({ [FORM.put_payment]: FORM_STATUS.success }));
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.payment_update_success',
      }));
      dispatch(closeModal({ modal: MODALS.editPayment }));
    } else if (response.status === 400) {
      dispatch(setError({ [FORM.put_payment]: response.errors.fields }));
      dispatch(setFormStatus({ [FORM.put_payment]: FORM_STATUS.error }));
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: Object.values(response.errors)[0],
      }));
    } else {
      dispatch(setFormStatus({ [FORM.put_payment]: FORM_STATUS.error }));
    }
  } catch (e) { /* Log the error here */
    dispatch(setFormStatus({ [FORM.put_payment]: FORM_STATUS.error }));
  }
};

export const postPurchaseAction: (
  purchase: PurchasePayload,
  modal?: MODALS,
  reload?: () => void,
) => ThunkedAction<State> =
(
  purchase: PurchasePayload,
  modal?: MODALS,
  reload?: () => void,
) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setFormStatus({ [FORM.post_purchase]: FORM_STATUS.processing }));
    const organisation = getState().currentOrganisation.id;
    const response = await postPurchaseData(organisation, purchase);
    if (response.success) {
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.purchased_success',
      }));
      dispatch(setFormStatus({ [FORM.post_purchase]: FORM_STATUS.success }));
      dispatch(getEntitiesAction(ENTITIES.payments));
      dispatch(getEntitiesAction(ENTITIES.invoices));
      if (!!reload) {
        reload();
      }
      if (!!modal) {
        dispatch(closeModal({ modal }));
      }
    } else {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.purchased_error',
      }));
      Debug.errorLog(response);
      dispatch(setFormStatus({ [FORM.post_purchase]: FORM_STATUS.error }));
    }
  } catch (e: unknown) { /* Log the error here */
    dispatch(setFormStatus({ [FORM.post_purchase]: FORM_STATUS.error }));
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.purchased_error',
    }));
    if (e instanceof Error) {
      Debug.errorLog(e.message);
      return;
    }
    Debug.errorLog(e);
  }
};
